import { DOCUMENT } from '@angular/common';
import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { Subject, combineLatest, filter, map, takeUntil, firstValueFrom } from 'rxjs';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { CenteredLayoutComponent } from './layouts/horizontal/centered/centered.component';
import { EnterpriseLayoutComponent } from './layouts/horizontal/enterprise/enterprise.component';
import { MaterialLayoutComponent } from './layouts/horizontal/material/material.component';
import { ModernLayoutComponent } from './layouts/horizontal/modern/modern.component';
import { ClassicLayoutComponent } from './layouts/vertical/classic/classic.component';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import { CompactLayoutComponent } from './layouts/vertical/compact/compact.component';
import { DenseLayoutComponent } from './layouts/vertical/dense/dense.component';
import { FuturisticLayoutComponent } from './layouts/vertical/futuristic/futuristic.component';
import { ThinLayoutComponent } from './layouts/vertical/thin/thin.component';
import { TermosUsoModalComponent } from 'app/shared/components/termos-uso-modal/termos-uso-modal.component';
import { UsuarioService } from 'app/shared/services/edduca-imob/usuario/usuario.service';
import { AuthService } from 'app/shared/services/core/auth.service';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { TokenVO } from 'app/shared/models/core';
import { OnboardingTourComponent } from 'app/shared/components/onboarding-tour/onboarding-tour.component';

@Component({
    selector: 'layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        EmptyLayoutComponent,
        CenteredLayoutComponent,
        EnterpriseLayoutComponent,
        MaterialLayoutComponent,
        ModernLayoutComponent,
        ClassicLayoutComponent,
        ClassyLayoutComponent,
        CompactLayoutComponent,
        DenseLayoutComponent,
        FuturisticLayoutComponent,
        ThinLayoutComponent,
        OnboardingTourComponent,
    ],
})
export class LayoutComponent implements OnInit, OnDestroy {
    @ViewChild('onboardingTour') onboardingTour: OnboardingTourComponent;
    
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private termosVerificados = 'False';

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private _dialog: MatDialog,
        private _usuarioService: UsuarioService,
        private _authService: AuthService,
        private _fuseConfirmationService: FuseConfirmationService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the theme and scheme based on the configuration
        combineLatest([
            this._fuseConfigService.config$,
            this._fuseMediaWatcherService.onMediaQueryChange$([
                '(prefers-color-scheme: dark)',
                '(prefers-color-scheme: light)',
            ]),
        ])
            .pipe(
                takeUntil(this._unsubscribeAll),
                map(([config, mql]) => {
                    const options = {
                        scheme: config.scheme,
                        theme: config.theme,
                    };

                    // If the scheme is set to 'auto'...
                    if (config.scheme === 'auto') {
                        // Decide the scheme using the media query
                        options.scheme = mql.breakpoints[
                            '(prefers-color-scheme: dark)'
                        ]
                            ? 'dark'
                            : 'light';
                    }

                    return options;
                })
            )
            .subscribe((options) => {
                // Store the options
                this.scheme = options.scheme;
                this.theme = options.theme;

                // Update the scheme and theme
                this._updateScheme();
                this._updateTheme();
            });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                // Update the layout
                this._updateLayout();
            });

        // Set the app version
        this._renderer2.setAttribute(
            this._document.querySelector('[ng-version]'),
            'fuse-version',
            FUSE_VERSION
        );

        // Set the OS name
        this._renderer2.addClass(
            this._document.body,
            this._fusePlatformService.osName
        );

        // Verificar termos de uso
        this._verificarTermosUso();

        // Iniciar tour de onboarding apenas se os termos já foram aceitos
        // (caso contrário, o tour será iniciado após o aceite dos termos)
        setTimeout(() => {
            const currentUser = this._authService.getUserInToken();
            if (currentUser && currentUser.termosAceitos === 'True') {
                this._iniciarTourOnboarding();
            }
        }, 2000);
    }

    /**
     * Verifica se o usuário já aceitou os termos de uso
     */
    private _verificarTermosUso(): void {
        // Aguardar um pouco para garantir que o token está carregado
        setTimeout(() => {
            if (this.termosVerificados == 'False' && this._authService.isTokenValid()) {
                const currentUser = this._authService.getUserInToken();
                
                if (currentUser && currentUser.userId && currentUser.termosAceitos == 'False') {
                    this.termosVerificados = 'True';
                    this._exibirModalTermos();
                }
            }
        }, 500);
    }

    /**
     * Exibe o modal de termos de uso
     */
    private _exibirModalTermos(): void {
        const dialogRef = this._dialog.open(TermosUsoModalComponent, {
            width: '800px',
            maxHeight: '90vh',
            disableClose: true
        });

        dialogRef.afterClosed().subscribe((aceito: boolean) => {
            if (aceito) {
                // Usuário aceitou os termos
                this._aceitarTermos();
            } else {
                // Usuário recusou os termos
                this._exibirAvisoRecusa();
            }
        });
    }

    /**
     * Registra o aceite dos termos
     */
    private async _aceitarTermos(): Promise<void> {
        try {
            const currentUser = this._authService.getUserInToken();
            
            if (currentUser && currentUser.userId) {
                const response = await firstValueFrom(this._usuarioService.aceitarTermosUso(currentUser.userId));
                
                // Atualizar o token com o novo JWT retornado pelo backend
                if (response.token) {
                    this._authService.setToken(new TokenVO({ token: response.token }));
                }
                
                // Termos aceitos com sucesso - iniciar o tour de onboarding
                // Aguarda um tempo para o DOM estar pronto e a navegação carregar
                setTimeout(() => {
                    this._iniciarTourOnboarding();
                }, 1500);
            }
        } catch (error) {
            console.error('Erro ao aceitar termos:', error);
        }
    }

    /**
     * Inicia o tour de onboarding para novos usuários
     */
    private _iniciarTourOnboarding(): void {
        if (this.onboardingTour && !this.onboardingTour.isTourCompleted()) {
            this.onboardingTour.startTour();
        }
    }

    /**
     * Exibe aviso quando o usuário recusa os termos
     */
    private _exibirAvisoRecusa(): void {
        const confirmation = this._fuseConfirmationService.open({
            title: 'Termos de Uso Necessários',
            message: 'Para acessar os conteúdos da plataforma EDDUCA IMOB, é necessário aceitar os Termos de Uso. Se você não aceitar, será desconectado da plataforma. Deseja realmente recusar?',
            icon: {
                show: true,
                name: 'heroicons_outline:exclamation-triangle',
                color: 'warn'
            },
            actions: {
                confirm: {
                    show: true,
                    label: 'Sim, recusar',
                    color: 'warn'
                },
                cancel: {
                    show: true,
                    label: 'Voltar aos termos'
                }
            },
            dismissible: false
        });

        confirmation.afterClosed().subscribe((result) => {
            if (result === 'confirmed') {
                // Deslogar o usuário usando o método clearToken
                this._authService.clearToken();
            } else {
                // Reabrir o modal de termos
                this._exibirModalTermos();
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void {
        // Get the current activated route
        let route = this._activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if (layoutFromQueryParam) {
            this.layout = layoutFromQueryParam;
            if (this.config) {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if (
                path.routeConfig &&
                path.routeConfig.data &&
                path.routeConfig.data.layout
            ) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if (className.startsWith('theme-')) {
                this._document.body.classList.remove(
                    className,
                    className.split('-')[1]
                );
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
    }
}
